import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Upload, User, Phone, Mail, Briefcase } from 'lucide-react';

export default function CustomerDeliverySetup() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [proofDocument, setProofDocument] = useState(null);
  const [proofDocumentUrl, setProofDocumentUrl] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    role: '',
    contact_number: '',
    company_email: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    // Validate file size (8MB)
    const maxSize = 8 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must not exceed 8MB');
      return;
    }

    setProofDocument(file);
    
    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/proof-document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setProofDocumentUrl(response.data.document_url);
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
      setProofDocument(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate proof document is uploaded (MANDATORY)
    if (!proofDocumentUrl) {
      toast.error('⚠️ Proof of Role/Designation document is required');
      setLoading(false);
      return;
    }

    // Validate contact number
    const contactNumber = formData.contact_number.trim();
    if (contactNumber.length !== 7) {
      toast.error('⚠️ Contact number must be exactly 7 digits');
      setLoading(false);
      return;
    }
    if (!contactNumber.startsWith('7') && !contactNumber.startsWith('9')) {
      toast.error('⚠️ Write your mobile number (must start with 7 or 9)');
      setLoading(false);
      return;
    }

    try {
      const deliveryDetails = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        contact_number: formData.contact_number,
        company_email: formData.company_email,
        proof_document_url: proofDocumentUrl
      };

      await axios.put(`${API}/customer/delivery-details`, deliveryDetails, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profile details saved successfully!');
      
      // Redirect to homepage
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Complete Your Profile</h1>
          <p className="text-slate-600">
            Please provide your employment details to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <User className="w-4 h-4" />
              First Name *
            </Label>
            <Input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              placeholder="Enter your first name"
              className="h-12"
            />
          </div>

          {/* Last Name (Including Middle Name) */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <User className="w-4 h-4" />
              Last Name (Including Middle Name) *
            </Label>
            <Input
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              placeholder="Enter your last name and middle name"
              className="h-12"
            />
          </div>

          {/* Role */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <Briefcase className="w-4 h-4" />
              Role *
            </Label>
            <Input
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              placeholder="Enter your role/designation"
              className="h-12"
            />
          </div>

          {/* Contact Number */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <Phone className="w-4 h-4" />
              Contact Number *
            </Label>
            <Input
              name="contact_number"
              value={formData.contact_number}
              onChange={handleChange}
              required
              placeholder="7 digits (starts with 7 or 9)"
              maxLength={7}
              pattern="[79][0-9]{6}"
              className="h-12"
            />
            <p className="text-xs text-slate-500 mt-1">
              Must be 7 digits and start with 7 or 9 (excluding country code)
            </p>
          </div>

          {/* Company Email */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <Mail className="w-4 h-4" />
              Company Email *
            </Label>
            <Input
              name="company_email"
              type="email"
              value={formData.company_email}
              onChange={handleChange}
              required
              placeholder={user?.email ? `example@${user.email.split('@')[1]}` : 'your.email@company.com'}
              className="h-12"
            />
            <p className="text-xs text-slate-500 mt-1">
              Must use the same domain as your registered email
              {user?.email && ` (@${user.email.split('@')[1]})`}
            </p>
          </div>

          {/* Proof of Role/Designation - MANDATORY */}
          <div>
            <Label className="flex items-center gap-2 text-slate-700 mb-2">
              <FileText className="w-4 h-4" />
              Proof of Role/Designation *
            </Label>
            
            {/* Red Notice */}
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm font-medium">
                ⚠️ If the person is single (Sole proprietor), submit valid (less than 1 month) business profile from Economic Ministry.
              </p>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                id="proof-upload"
                disabled={uploading}
                required
              />
              <label 
                htmlFor="proof-upload" 
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Upload className={`w-8 h-8 ${uploading ? 'text-slate-400' : 'text-emerald-600'}`} />
                {uploading ? (
                  <p className="text-slate-600">Uploading...</p>
                ) : proofDocument ? (
                  <>
                    <p className="text-emerald-600 font-medium">{proofDocument.name}</p>
                    <p className="text-xs text-slate-500">Click to change file</p>
                  </>
                ) : (
                  <>
                    <p className="text-slate-700 font-medium">
                      Click to upload proof document (Required)
                    </p>
                    <p className="text-xs text-slate-500">
                      PDF only, max 8MB
                    </p>
                  </>
                )}
              </label>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              A written document from the Company or HR with a stamp stating your employment and role.
              {!proofDocument && " You can upload this later from your dashboard."}
            </p>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-14 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-lg font-semibold"
            disabled={loading || uploading}
          >
            {loading ? 'Saving...' : 'Save and Continue'}
          </Button>
        </form>

        <p className="text-xs text-center text-slate-500 mt-6">
          * Required fields. Your information will be kept secure and used only for verification purposes.
        </p>
      </Card>
    </div>
  );
}
